import { useState, useEffect } from 'react';
import { getStorageUsage } from '../services/gtfs-static';

interface StorageManagerProps {
    isOpen: boolean;
    onClose: () => void;
}

export function StorageManager({ isOpen, onClose }: StorageManagerProps) {
    const [usage, setUsage] = useState<number>(0);
    const [quota, setQuota] = useState<number>(0);
    const [operatorUsage, setOperatorUsage] = useState<Record<string, number>>({});

    const loadUsage = async () => {
        const stats = await getStorageUsage();
        setUsage(stats.usage);
        setQuota(stats.quota);
        setOperatorUsage({});
    };

    useEffect(() => {
        if (isOpen) {
            loadUsage();
        }
    }, [isOpen]);

    const formatBytes = (bytes: number) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    if (!isOpen) return null;

    const percentage = quota > 0 ? (usage / quota) * 100 : 0;

    // Calculate totals
    const totalEstimated = Object.values(operatorUsage).reduce((a, b) => a + b, 0);

    // If estimated usage is higher than actual usage, it implies compression or efficient storage.
    // We scale down the estimates to match the actual reported usage.
    // If actual is higher, the difference is overhead.
    const compressionRatio = (usage > 0 && totalEstimated > usage) ? (usage / totalEstimated) : 1;

    const adjustedTotalGtfs = totalEstimated * compressionRatio;
    const overhead = Math.max(0, usage - adjustedTotalGtfs);

    return (
        <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000, // High z-index to be above everything
            padding: '16px'
        }}>
            <div style={{
                backgroundColor: '#1a1a1a',
                border: '1px solid #333',
                borderRadius: '8px',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                width: '100%',
                maxWidth: '448px', // max-w-md
                overflow: 'hidden',
                maxHeight: '90vh', // Limit height for scrolling
                display: 'flex',
                flexDirection: 'column'
            }}>
                <div style={{
                    padding: '16px',
                    borderBottom: '1px solid #333',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexShrink: 0
                }}>
                    <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: 'white', margin: 0 }}>Storage Management</h2>
                    <button
                        onClick={onClose}
                        style={{
                            color: '#9ca3af',
                            background: 'none',
                            border: 'none',
                            fontSize: '18px',
                            cursor: 'pointer',
                            padding: '4px'
                        }}
                    >
                        ✕
                    </button>
                </div>

                <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '24px', overflowY: 'auto' }}>
                    {/* Usage Stats */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#d1d5db' }}>
                            <span>Total Used: {formatBytes(usage)}</span>
                            <span>Quota: {formatBytes(quota)}</span>
                        </div>

                        {/* Multi-colored Progress Bar */}
                        <div style={{ height: '8px', backgroundColor: '#333', borderRadius: '9999px', overflow: 'hidden', display: 'flex' }}>
                            {/* GTFS Data Part */}
                            <div
                                style={{
                                    height: '100%',
                                    backgroundColor: '#3b82f6', // Blue
                                    width: `${quota > 0 ? (adjustedTotalGtfs / quota) * 100 : 0}%`,
                                    transition: 'width 500ms'
                                }}
                                title={`GTFS Data: ${formatBytes(adjustedTotalGtfs)}`}
                            />
                            {/* Overhead/Other Part */}
                            <div
                                style={{
                                    height: '100%',
                                    backgroundColor: '#6b7280', // Gray
                                    width: `${quota > 0 ? (overhead / quota) * 100 : 0}%`,
                                    transition: 'width 500ms'
                                }}
                                title={`Overhead/Other: ${formatBytes(overhead)}`}
                            />
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                            <div style={{ display: 'flex', gap: '12px' }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#3b82f6' }}></span>
                                    Data: {formatBytes(adjustedTotalGtfs)}
                                </span>
                                {overhead > 0 && (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#6b7280' }}></span>
                                        Overhead: {formatBytes(overhead)}
                                    </span>
                                )}
                            </div>
                            <span>{percentage.toFixed(1)}% of quota</span>
                        </div>
                        {compressionRatio < 1 && (
                            <div style={{ fontSize: '10px', color: '#6b7280', fontStyle: 'italic' }}>
                                * Data is compressed on disk ({(compressionRatio * 100).toFixed(0)}% of original size)
                            </div>
                        )}
                    </div>

                    {/* Info Note */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>Storage Info</h3>
                        <p style={{ color: '#d1d5db', fontSize: '14px', margin: 0, lineHeight: '1.5' }}>
                            This shows your browser's local storage usage. Static GTFS data is fetched from Cloudflare R2 and is not cached locally.
                        </p>
                    </div>
                </div>

                <div style={{ padding: '16px', borderTop: '1px solid #333', backgroundColor: '#222', fontSize: '12px', color: '#6b7280' }}>
                    Data is loaded directly from cloud storage on-demand.
                </div>
            </div>
        </div>
    );
}

